import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { User, Mail, Lock, Save, CheckCircle2 } from 'lucide-react';

export default function ProfilePage() {
  const { api, user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    new_name: user?.name || '',
    new_email: user?.email || '',
    new_password: '',
    confirm_password: '',
    current_password: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.current_password) {
      setError('Current password is required to make any change.');
      return;
    }
    if (form.new_password && form.new_password !== form.confirm_password) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (form.new_password && form.new_password.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }

    const payload = {
      current_password: form.current_password,
      new_email: form.new_email !== user?.email ? form.new_email : '',
      new_name: form.new_name !== user?.name ? form.new_name : '',
      new_password: form.new_password || '',
    };

    if (!payload.new_email && !payload.new_name && !payload.new_password) {
      setError('Nothing to change. Edit a field above first.');
      return;
    }

    setSaving(true);
    try {
      await api.put('/auth/me', payload);
      setSuccess('Profile updated. Your new credentials are now active.');
      setForm({ ...form, current_password: '', new_password: '', confirm_password: '' });
      if (refreshUser) await refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not update profile.');
    }
    setSaving(false);
  };

  return (
    <div data-testid="profile-page" className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">My Profile</h1>
        <p className="text-sm text-slate-500 mt-1 font-medium">
          Change your login email, display name, or password. Role: <span className="capitalize font-semibold text-slate-700">{user?.role?.replace('_', ' ')}</span>
        </p>
      </div>

      <form onSubmit={handleSave} className="bg-white border border-[#E2E2DF] rounded-md p-6 space-y-5">
        {error && (
          <div data-testid="profile-error" className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
            {error}
          </div>
        )}
        {success && (
          <div data-testid="profile-success" className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 rounded text-sm flex items-center gap-2">
            <CheckCircle2 size={16} /> {success}
          </div>
        )}

        <div>
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
            <User size={13} /> Display Name
          </label>
          <input
            data-testid="profile-name-input"
            type="text"
            value={form.new_name}
            onChange={e => setForm({ ...form, new_name: e.target.value })}
            className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:ring-2 focus:ring-[#D35400] focus:outline-none"
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
            <Mail size={13} /> Login Email
          </label>
          <input
            data-testid="profile-email-input"
            type="email"
            value={form.new_email}
            onChange={e => setForm({ ...form, new_email: e.target.value })}
            className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:ring-2 focus:ring-[#D35400] focus:outline-none"
          />
          <p className="text-xs text-slate-400 mt-1">You'll use this email to log in from now on.</p>
        </div>

        <div className="border-t border-[#E2E2DF] pt-5">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-3 flex items-center gap-2">
            <Lock size={13} /> Change Password (optional)
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1.5">New Password</label>
              <input
                data-testid="profile-new-password-input"
                type="password"
                value={form.new_password}
                onChange={e => setForm({ ...form, new_password: e.target.value })}
                placeholder="Leave blank to keep current"
                className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:ring-2 focus:ring-[#D35400] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1.5">Confirm New Password</label>
              <input
                data-testid="profile-confirm-password-input"
                type="password"
                value={form.confirm_password}
                onChange={e => setForm({ ...form, confirm_password: e.target.value })}
                placeholder="Repeat new password"
                className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:ring-2 focus:ring-[#D35400] focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div className="border-t border-[#E2E2DF] pt-5 bg-amber-50/50 -mx-6 px-6 pb-1">
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-amber-800 mb-1.5">
            <Lock size={13} /> Current Password (required to save)
          </label>
          <input
            data-testid="profile-current-password-input"
            type="password"
            value={form.current_password}
            onChange={e => setForm({ ...form, current_password: e.target.value })}
            required
            autoComplete="current-password"
            className="w-full px-3 py-2 border border-amber-300 rounded-md text-sm focus:ring-2 focus:ring-amber-600 focus:outline-none bg-white"
          />
          <p className="text-xs text-amber-700/70 mt-1">For security, enter your current password to confirm any change.</p>
        </div>

        <div className="flex justify-end pt-2">
          <button
            data-testid="profile-save-button"
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-[#D35400] hover:bg-[#A04000] disabled:bg-slate-300 text-white rounded-md text-sm font-semibold transition-all duration-200 shadow-sm hover:shadow-md hover:shadow-orange-500/20"
          >
            <Save size={16} strokeWidth={2.25} /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
